import * as Dialog from '@radix-ui/react-dialog'
import { type ConsumerTradeInsQuery, TradeInStatusEnum } from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { usePortalContainer } from '../providers/portal'
import { IconX } from '../ui/icons'
import { OverlayReporter } from './OverlayReporter'
import { SendInInstructions } from './SendInInstructions'

/** One past trade-in from the `ConsumerTradeIns` query (non-null). */
export type ConsumerTradeIn = NonNullable<NonNullable<ConsumerTradeInsQuery['ConsumerTradeIns']>[number]>

/** Where the parcel is in its journey — the customer-facing distinction the status chip/copy use. */
export type TradeInProgress = 'awaiting' | 'transit' | 'received'

/**
 * Collapse Core's `TradeIn.status` (INITIAL/TRANSIT/RECEIVED/DELIVERED) + the `registered` flag into
 * the three states we show: `awaiting` (registered but we haven't seen the parcel move yet),
 * `transit` (on its way), `received` (arrived / processed). Defaults to `awaiting` when status is absent.
 */
export function tradeInProgress(ti: ConsumerTradeIn): TradeInProgress {
  if (ti.registered || ti.status === TradeInStatusEnum.Received || ti.status === TradeInStatusEnum.Delivered) {
    return 'received'
  }
  if (ti.status === TradeInStatusEnum.Transit) return 'transit'
  return 'awaiting'
}

const localeTag: Record<string, string> = { da: 'da-DK', en: 'en-GB', de: 'de-DE' }

/** Full readable date for the trade-in (e.g. "14 Jul 2026"). Empty if missing/invalid. */
function formatDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(localeTag[locale] ?? 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d)
}

interface TradeInDetailDialogProps {
  /** The trade-in to inspect, or `null` when the dialog is closed. */
  tradeIn: ConsumerTradeIn | null
  onClose: () => void
}

/**
 * "My trade-ins" detail: when it was registered, what the customer should do now (with the send-in
 * code/QR/label if they still need to ship it), and the list of items in the trade-in — titles only.
 * A controlled Radix dialog (open when `tradeIn` is set), portalled into the shadow-root container.
 */
export function TradeInDetailDialog({ tradeIn, onClose }: TradeInDetailDialogProps) {
  const container = usePortalContainer()
  const { t, locale } = useTranslation()

  const isPickup =
    tradeIn?.shippingCarrier?.carrierId === 'pickup' || tradeIn?.shippingCarrier?.customCarrier === true
  const progress: TradeInProgress = tradeIn ? tradeInProgress(tradeIn) : 'awaiting'
  const items = (tradeIn?.items ?? []).flatMap((i) => (i ? [i] : []))
  // What to tell the customer: received → done; in transit → we'll let you know; else drop off / ship it.
  const nextKey =
    progress === 'received'
      ? 'myTradeIns.nextReceived'
      : progress === 'transit'
        ? 'myTradeIns.nextTransit'
        : isPickup
          ? 'myTradeIns.nextPickup'
          : 'myTradeIns.nextShip'
  // The send-in code/QR/label only makes sense while we're still awaiting a shipped parcel.
  const showSendIn = progress === 'awaiting' && !isPickup

  return (
    // modal={false} so Radix's scroll-lock doesn't block the wheel in our shadow root (see TermsDialog).
    <Dialog.Root open={!!tradeIn} onOpenChange={(open) => !open && onClose()} modal={false}>
      <Dialog.Portal container={container ?? undefined}>
        <div className="modal-scrim" aria-hidden onClick={onClose} />
        <Dialog.Content className="modal tradein-detail-modal" aria-describedby={undefined}>
          <OverlayReporter />
          <div className="modal-head">
            <Dialog.Title asChild>
              <h2>{t('myTradeIns.detailTitle')}</h2>
            </Dialog.Title>
            <Dialog.Close className="icon-btn" aria-label={t('terms.close')}>
              <IconX />
            </Dialog.Close>
          </div>

          <div className="modal-body tradein-detail">
            <p className="tid-date">
              {t('myTradeIns.tradeInPrefix')} · {formatDate(tradeIn?.dateTime, locale)}
            </p>

            <div className="tid-next">
              <h3>{t('myTradeIns.nextHeading')}</h3>
              <p>{t(nextKey)}</p>
              {showSendIn && (
                <SendInInstructions
                  label={tradeIn?.label}
                  isPickup={isPickup}
                  whenPending={<p className="tid-note">{t('myTradeIns.labelUnavailable')}</p>}
                />
              )}
            </div>

            <div className="tid-items">
              <h3>{t('myTradeIns.itemsHeading')}</h3>
              <ul className="tid-item-list">
                {items.map((item) => {
                  const image = item.imageUrls?.[0] || item.masterVariantImageUrl
                  return (
                    <li className="tid-item-row" key={item._id}>
                      {image ? (
                        <img className="tid-item-thumb" src={image} alt="" />
                      ) : (
                        <span className="tid-item-thumb tid-item-thumb--empty" aria-hidden />
                      )}
                      <span className="tid-item-name">{item.name}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>

          <div className="modal-foot">
            <Dialog.Close asChild>
              <button type="button" className="btn btn-primary">
                {t('myTradeIns.close')}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
